import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Card, CardContent, Typography, Divider, CircularProgress, Dialog, DialogTitle as MuiDialogTitle, DialogContent as MuiDialogContent, DialogActions as MuiDialogActions } from '@material-ui/core';

import * as ActionsDialog from 'app/store/actions';
import * as Actions from './store/actions';
import { getAiFeaturesEnabledStatus, getErrorReportingSettings, sendErrorReport } from 'app/main/settings/store/actions/general.actions';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(() => ({
	logFont: {
		fontFamily: 'Ubuntu Mono'
	},
	logWrapper: {
		scrollBehavior: 'smooth'
	},
	aiAnalysisCard: {
		backgroundColor: '#f5f5f5',
		marginBottom: '16px',
		border: '1px solid #ddd'
	},
	aiAnalysisTitle: {
		color: '#1976d2',
		fontWeight: 'bold'
	},
	errorAnalysisText: {
		fontFamily: 'monospace',
		backgroundColor: '#fff',
		padding: '8px',
		border: '1px solid #ccc',
		borderRadius: '4px',
		fontSize: '12px'
	},
	// Banner for rule-based deployment diagnosis. Lighter weight than the
	// AI analysis card and visible whether or not AI features are enabled.
	diagnosisCard: {
		backgroundColor: '#fff8e1',
		marginBottom: '16px',
		border: '1px solid #ffb74d',
		borderLeft: '4px solid #ef6c00'
	},
	diagnosisTitle: {
		color: '#bf360c',
		fontWeight: 600,
		marginBottom: '4px'
	},
	diagnosisSnippet: {
		fontFamily: 'monospace',
		fontSize: '12px',
		backgroundColor: '#fff',
		padding: '8px',
		border: '1px solid #e0e0e0',
		borderRadius: '4px',
		marginTop: '8px',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word'
	}
}));

const Logs = props => {
	const classes = useStyles();
	const dispatch = useDispatch();
		const logsRef = useRef(null);
		const logsEndRef = useRef(null);

	const [atBottom, setAtBottom] = useState(false);
	const [firstRender, setFirstRender] = useState(true);
	const [showFullLogs, setShowFullLogs] = useState(true); // Will be managed by useEffect after data loads
	const [hasBeenAutoHidden, setHasBeenAutoHidden] = useState(false);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [errorReportingEnabled, setErrorReportingEnabled] = useState(false);
	const [reportSent, setReportSent] = useState(false);
	const [reportSending, setReportSending] = useState(false);
	const [showReportPreview, setShowReportPreview] = useState(false);
	const rawLogs = useSelector(({ clusters }) => clusters.cluster.logs);
	const logsClusterId = useSelector(({ clusters }) => clusters.cluster.logsClusterId);
	// Only use logs if they belong to the current cluster (prevents log leakage between clusters)
	const logs = (logsClusterId === props.cluster?.id || logsClusterId === null) ? rawLogs : null;
	const aiErrorAnalysis = useSelector(({ clusters }) => clusters.cluster.aiErrorAnalysis);	const aiFeaturesEnabled = useSelector(({ settings }) => settings.settings.aiFeaturesEnabled);

	// Get live cluster data from Redux store instead of using static props
	// This ensures we get real-time updates when cluster state changes
	const currentCluster = useSelector(({ clusters }) => {
		// First try to get the single cluster if it matches the ID we're looking for
		const singleCluster = clusters.cluster.cluster;
		if (singleCluster?.id === props.cluster?.id) {
			return singleCluster;
		}
		
		// Otherwise, find it in the clusters list
		const clusterFromList = clusters.cluster.list?.find(c => c.id === props.cluster?.id);
		return clusterFromList || props.cluster; // Fallback to props if not found
	});

	const isClusterFailed = currentCluster?.info?.state === 'failed';
	const hasAnalysisForCluster = aiErrorAnalysis?.clusterId === currentCluster?.id;
	const hasAnyAnalysis = aiErrorAnalysis && aiErrorAnalysis.analysis;
	
	// If we have AI analysis for this cluster, treat it as "failed" for UI purposes
	// BUT only if the cluster is actually failed or deploying (not destroyed/down)
	const shouldShowAsFailedCluster = isClusterFailed || (hasAnalysisForCluster && (currentCluster?.info?.state === 'deploying' || currentCluster?.info?.state === 'failed'));

	// Fetch AI features status on component mount
	useEffect(() => {
		dispatch(getAiFeaturesEnabledStatus());
	}, [dispatch]);

	// Fetch error reporting opt-in setting on mount
	useEffect(() => {
		dispatch(getErrorReportingSettings()).then(data => {
			if (data) setErrorReportingEnabled(data.enabled || false);
		});
	}, [dispatch]);

	const buildReportPayload = () => ({
		event_type: 'error.cluster_deploy',
		message: 'Cluster deployment failed - ' + (currentCluster?.provider || 'unknown') + ' ' + (currentCluster?.info?.terraform?.region || ''),
		details: {
			heidi_version: process.env.REACT_APP_VERSION || 'unknown',
			provider: currentCluster?.provider || 'unknown',
			region: currentCluster?.info?.terraform?.region || 'unknown',
			cluster_id_hash: currentCluster?.id ? btoa(currentCluster.id).slice(0, 16) : 'unknown',
			full_log: logs ? logs.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '') : '',
			timestamp: new Date().toISOString(),
		},
	});

	const handleSendReport = async () => {
		setReportSending(true);
		try {
			const payload = buildReportPayload();
			await dispatch(sendErrorReport(payload.event_type, payload.message, payload.details));
			setReportSent(true);
			setShowReportPreview(false);
		} finally {
			setReportSending(false);
		}
	};

	// Load cached AI analysis on component mount (separate from real-time logic)
	useEffect(() => {
		if (currentCluster?.id && logs) {
			dispatch(Actions.loadCachedAiAnalysis(currentCluster.id, logs));
		}
	}, [dispatch, currentCluster?.id]); // Only run on mount, not when logs or isClusterFailed change

	// Handle initial state - hide logs if this is a failed cluster with AI enabled
	useEffect(() => {
		if (aiFeaturesEnabled !== null && aiFeaturesEnabled && isClusterFailed) {
			setShowFullLogs(false);
			setHasBeenAutoHidden(true);
		}
	}, [aiFeaturesEnabled, isClusterFailed]);

	// This effect ensures that when a cluster fails, the logs are automatically
	// hidden to show the AI analysis, but only once. If the user chooses to
	// see the full logs again, they will not be re-hidden on subsequent renders.
	// Also clears AI analysis when cluster is destroyed/down.
	useEffect(() => {
		const clusterState = currentCluster?.info?.state;
		
		// Clear AI analysis if cluster is destroyed/down
		if (clusterState === 'down' || clusterState === 'destroyed') {
			dispatch(Actions.clearAiErrorAnalysis(currentCluster?.id));
			setShowFullLogs(true);
			setHasBeenAutoHidden(false);
			return;
		}
		
		if (aiFeaturesEnabled && shouldShowAsFailedCluster && hasAnyAnalysis && !hasBeenAutoHidden) {
			setShowFullLogs(false);
			setHasBeenAutoHidden(true);
		} else if (!shouldShowAsFailedCluster) {
			// If the cluster is not in a failed state (e.g., deploying), we ensure
			// the logs are visible and reset the auto-hide flag for future failures.
			setShowFullLogs(true);
			setHasBeenAutoHidden(false);
		}
	}, [aiFeaturesEnabled, shouldShowAsFailedCluster, hasAnyAnalysis, hasBeenAutoHidden, currentCluster?.info?.state, dispatch, currentCluster?.id]);
	
	// Automatically trigger AI analysis for failed clusters when logs are available
	useEffect(() => {


		if (aiFeaturesEnabled && isClusterFailed && logs && !hasAnalysisForCluster && !isAnalyzing) {
			setIsAnalyzing(true);
			dispatch(Actions.analyzeTerraformError(currentCluster.id, logs))
				.then(result => {
					setIsAnalyzing(false);
	
				})
				.catch(err => {
					console.error(err);
					setIsAnalyzing(false);
				});
		}
	}, [aiFeaturesEnabled, isClusterFailed, logs, hasAnalysisForCluster, isAnalyzing, dispatch, currentCluster?.id]);

		const closeLogs = () => {
			dispatch(Actions.clearLogs());
			dispatch(ActionsDialog.closeDialog());
		};

		const scrollToBottom = () => {
			if (logsRef.current) {
			  logsRef.current.scrollTop = logsRef.current.scrollHeight + 200;
			}
		  };

		const setBottom = ({ target }) => {
			const { scrollHeight, scrollTop, clientHeight } = target;
			const threshold = 100;
		  
			setAtBottom(scrollHeight - scrollTop <= clientHeight + threshold);
		  };

		useEffect(() => {
			if (firstRender && logs) {
				scrollToBottom();
				setFirstRender(false);
			}
		}, [firstRender, setFirstRender, logs]);

		useEffect(() => {
			const { type } = props;
			const timer = setTimeout(
				() => dispatch(Actions.getLogsRepeat({ id: currentCluster.id, type })).then(() => atBottom && scrollToBottom()),
				10000
			);
			return () => clearTimeout(timer);
		}, [dispatch, props, logs, atBottom, currentCluster?.id]); // leave logs in in order to trigger action

		const diagnosis = currentCluster?.info?.deploymentFailure?.diagnosis;

		// Diagnosis banner rendered inline inside the log stream, right after
		// the terraform error block. Extracted so we can render either inline
		// (when we can locate the error block in the log HTML) or fall back to
		// the top of the log pane if no anchor is found.
		const diagnosisBanner = diagnosis?.summary ? (
			<Card className={classes.diagnosisCard}>
				<CardContent>
					<Typography variant="h6" className={classes.diagnosisTitle}>
						<span role="img" aria-label="warning">⚠️</span>{' '}
						{diagnosis.summary}
					</Typography>
					{diagnosis.suggestion && (
						<Typography variant="body2" style={{ color: '#3e2723', lineHeight: 1.55 }}>
							{diagnosis.suggestion}
						</Typography>
					)}
					{diagnosis.rawSnippet && (
						<details style={{ marginTop: '8px' }}>
							<summary style={{ cursor: 'pointer', fontSize: '12px', color: '#5d4037' }}>
								View raw error from {diagnosis.provider || currentCluster?.provider || 'provider'}
							</summary>
							<div className={classes.diagnosisSnippet}>
								{diagnosis.rawSnippet}
							</div>
						</details>
					)}
				</CardContent>
			</Card>
		) : null;

		// Split the log HTML at the first terraform error block so the banner
		// can sit just below the error that triggered it. Terraform formats
		// errors as a box: "│ Error: ..." lines closed by "╵". We anchor on
		// the closing "╵" (or its variants); if absent we fall back to the
		// "Error:" line, and ultimately to rendering the banner at the top.
		const logsParts = useMemo(() => {
			if (!logs || !diagnosis?.summary) return { before: logs, after: '', hasSplit: false };
			const closingIdx = (() => {
				for (const ch of ['╵', '┘', '└']) {
					const i = logs.indexOf(ch);
					if (i !== -1) return i;
				}
				return -1;
			})();
			let anchorEnd = -1;
			if (closingIdx !== -1) {
				// Cut after the line that contains the closing box char so the
				// banner appears immediately under the error block.
				const lineEnd = logs.indexOf('\n', closingIdx);
				anchorEnd = lineEnd === -1 ? logs.length : lineEnd + 1;
			} else {
				const errIdx = logs.search(/Error:/i);
				if (errIdx !== -1) {
					const lineEnd = logs.indexOf('\n', errIdx);
					anchorEnd = lineEnd === -1 ? logs.length : lineEnd + 1;
				}
			}
			if (anchorEnd === -1) return { before: logs, after: '', hasSplit: false };
			return {
				before: logs.substring(0, anchorEnd),
				after: logs.substring(anchorEnd),
				hasSplit: true
			};
		}, [logs, diagnosis?.summary]);

		return (
	<React.Fragment>
		{(!shouldShowAsFailedCluster || showFullLogs || !aiFeaturesEnabled) ? (
		<DialogTitle className="border-b-1">
			<div className="flex flex-row items-center justify-between">
				<span id="logs-header">{`Logs for ${currentCluster?.name || 'Cluster'}`}</span>
				<Button id="scroll-btn" variant="outlined" type="button" onClick={scrollToBottom}>
					Scroll To Bottom
				</Button>
			</div>
		</DialogTitle>
		) : 
		<div className='my-32' id="logs-header"/>
		}

			<DialogContent ref={logsRef} className={classes.logWrapper} onScroll={e => setBottom(e)}>
				{/* AI Analysis Progress Indicator - Only show if AI features are enabled */}
				{aiFeaturesEnabled && isAnalyzing && (
					<Card className={classes.aiAnalysisCard}>
						<CardContent>
							<Typography variant="h6" className={classes.aiAnalysisTitle}>
								<span role="img" aria-label="robot">🤖</span> Error Analysis
								<CircularProgress size={20} style={{ marginLeft: '8px' }} />
							</Typography>
							<Divider style={{ margin: '8px 0' }} />
							<Typography variant="body2" color="primary">
								Analyzing error... This may take a while.
							</Typography>
						</CardContent>
					</Card>
				)}
						{/* AI Error Analysis Results - Show prominently when we have analysis for this cluster */}
			{aiFeaturesEnabled && !isAnalyzing && hasAnalysisForCluster && hasAnyAnalysis && (
					<Card className={classes.aiAnalysisCard}>
						<CardContent>
							<Typography variant="h6" className={classes.aiAnalysisTitle}>
								<span role="img" aria-label="robot">🤖</span> Error Analysis
							</Typography>
							<Divider style={{ margin: '8px 0' }} />
							<Typography variant="body1" style={{ 
								marginBottom: '16px', 
								fontSize: '16px', 
								lineHeight: '1.6',
								fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
								color: '#2c3e50'
							}}>
								{aiErrorAnalysis.analysis?.data?.response || 
								 aiErrorAnalysis.analysis?.response || 
								 aiErrorAnalysis.analysis?.text || 
								 aiErrorAnalysis.analysis?.message || 
								 aiErrorAnalysis.analysis?.content ||
								 (typeof aiErrorAnalysis.analysis === 'string' ? aiErrorAnalysis.analysis : null) ||
								 'Analysis completed'}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								Analysis generated at: {new Date(aiErrorAnalysis.timestamp).toLocaleString()}
							</Typography>
						</CardContent>
					</Card>
				)}		{/* Show Full Logs Button - For failed clusters with AI enabled: show when logs are hidden */}
		{aiFeaturesEnabled && shouldShowAsFailedCluster && !showFullLogs && (
			<div style={{ textAlign: 'center', marginBottom: '16px' }}>
				<Button 
					variant="outlined" 
					onClick={() => setShowFullLogs(true)}
					size="large"
				>
					📋 Show Full Logs
				</Button>
			</div>
		)}

		{/* Full Logs - Show based on explicit logic */}
		{(!shouldShowAsFailedCluster || !aiFeaturesEnabled || showFullLogs) && (
				<>
					
					{/* Hide Logs Button - Only show for failed clusters when AI features are enabled */}
					{aiFeaturesEnabled && shouldShowAsFailedCluster && showFullLogs && (
						<div style={{ textAlign: 'center', marginBottom: '16px' }}>
							<Button 
								variant="outlined" 
								onClick={() => setShowFullLogs(false)}
								size="small"
							>
								🔼 Hide Logs
							</Button>
						</div>
					)}
						
						{/* Full logs — split at the terraform error block so the
						    rule-based diagnosis banner sits right under the
						    line that triggered it. If we couldn't find an
						    anchor, the banner falls back to the top of the
						    log pane (see fallback below the </>). */}
						{logsParts.hasSplit ? (
							<>
								<div id="logs" className={classes.logFont} dangerouslySetInnerHTML={{ __html: logsParts.before }}></div>
								{diagnosisBanner}
								<div id="logs-after" className={classes.logFont} dangerouslySetInnerHTML={{ __html: logsParts.after }}></div>
							</>
						) : (
							<>
								{diagnosisBanner}
								<div id="logs" className={classes.logFont} dangerouslySetInnerHTML={{ __html: logs }}></div>
							</>
						)}
						<div ref={logsEndRef} />
					</>
				)}
			</DialogContent>
			
			<DialogActions className="border-t-1">
				{isClusterFailed && !reportSent && (
					<Button
						variant="outlined"
						color="secondary"
						onClick={() => {
							if (errorReportingEnabled) {
								handleSendReport();
							} else {
								setShowReportPreview(true);
							}
						}}
						disabled={reportSending}
					>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Error Report
					</Button>
				)}
				{reportSent && (
					<Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
						Report sent.
					</Typography>
				)}
				<Button id="logs-close-btn" onClick={closeLogs} variant="outlined">
					Close
				</Button>
			</DialogActions>

			{/* Error Report Preview Dialog */}
			<MuiDialogTitle disableTypography style={{ display: 'none' }} />
			<Dialog open={showReportPreview} onClose={() => setShowReportPreview(false)} maxWidth="sm" fullWidth>
				<MuiDialogTitle>Send Error Report</MuiDialogTitle>
				<MuiDialogContent>
					<Typography variant="body2" style={{ marginBottom: 12 }}>
						This will send an anonymous error report to Heidi support containing:
					</Typography>
					<Typography variant="body2" component="ul" style={{ paddingLeft: 20, marginBottom: 12 }}>
						<li>Cloud provider and region</li>
						<li>Heidi version</li>
						<li>Last 50 lines of deployment logs</li>
						<li>Hashed cluster ID (not reversible)</li>
					</Typography>
					<Typography variant="caption" color="textSecondary">
						No IP addresses, credentials, or personal data are included. You can enable automatic error reporting in Settings &gt; General.
					</Typography>
				</MuiDialogContent>
				<MuiDialogActions>
					<Button onClick={() => setShowReportPreview(false)} variant="outlined">
						Cancel
					</Button>
					<Button onClick={handleSendReport} variant="contained" color="primary" disabled={reportSending}>
						{reportSending ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
						Send Report
					</Button>
				</MuiDialogActions>
			</Dialog>
		</React.Fragment>
	);
};

export default Logs;
